import { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { selectDocumentState, setContent, setDrafts, setJSONContent } from '../../redux/docSlice'
import { selectJWToken, selectUser, setShowNav } from '../../redux/systemSlice'
import Ai from '@tiptap-pro/extension-ai-advanced'
import { ASK_AI_DESCRIPTIONS, ASK_AI_TYPES, CONSTANTS } from '../../constants'
import './_Editable.scss'
import { useEditor, EditorContent, BubbleMenu } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Highlight from '@tiptap/extension-highlight'
import Placeholder from '@tiptap/extension-placeholder'
import StrikethroughSIcon from '@mui/icons-material/StrikethroughS'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import Dropdown from '../Dropdown/Dropdown'
import Button from '../Buttons'
import ControlledBubbleMenu from '../ControlledBubbleMenu/ControlledBubbleMenu'
import { useCookieUser } from '../../hooks/cookieUser'
import Diff from '../Diff/Diff'
import { htmlToString, completionToHTMLString } from '../../helpers/utility'
import { DocumentState } from '../../redux/types'
import ErrorModal from '../Modals/Error'
import { useFeatureFlagVariantKey, usePostHog } from 'posthog-js/react'
import Autocomplete from './Autocomplete'
import CopyEventHandler from './CopyEventHandler'
import { HoverMenuNode } from './HoverNode'
import EditableSpan from './EditableNode'
import TypingExtension from './TypeEventHandler'
import SlashCommand from './SlashCommand'
import DragHandle from '@tiptap-pro/extension-drag-handle-react'
import { LuPlus } from 'react-icons/lu'
import useContentItemActions from './useContentItemActions'
import { useData } from './useData'
import AddMore from '../AddMore/AddMore'
import EditorBar from './EditorBar'
import { FEATURE_FLAGS } from '../../hooks/featureFlags'
import SelectReferenceMenu from './SelectReferenceDropdown'
import { Skeleton, TextareaAutosize } from '@mui/material'
import CompleteModal from '../Modals/Complete'

type EditableProps = {
	docID: string
	type: string | undefined
}

function Document(props?: EditableProps) {
	const posthog = usePostHog()
	const dispatch = useDispatch()
	const user = useSelector(selectUser)
	const jwtToken = useSelector(selectJWToken)
	const documentState: DocumentState = useSelector((state) => selectDocumentState(state, props?.docID ?? ''))
	const { userIDCookie } = useCookieUser()
	const [askAIDropdownOpen, setAskAIDropdownOpen] = useState(false)
	const [askAIBubbleMenuOpen, setAskAIBubbleMenuOpen] = useState(false)
	const [addReferenceBubbleMenuOpen, setAddReferenceBubbleMenuOpen] = useState(false)
	const [askAIDropdownType, setAskAIDropdownType] = useState<string | null>(null)
	const closeAskAIDropdown = useRef(null)
	const [currSelection, setCurrSelection] = useState('')
	const [askAIGenerating, setAskAIGenerating] = useState(false)
	const [stagedContent, setStagedContent] = useState('')
	const [errorModalOpen, setErrorModalOpen] = useState(false)
	const [lastEditorContent, setLastEditorContent] = useState(CONSTANTS.EMPTY_DOCUMENT)
	const [showAutocompleteMenu, setShowAutocompleteMenu] = useState(false)
	const [showEditorBar, setShowEditorBar] = useState(false)
	const [showCompleteModal, setShowCompleteModal] = useState(false)
	const editorBarFF = useFeatureFlagVariantKey(FEATURE_FLAGS.EDITOR_BAR)
	const sentenceReferenceFF = useFeatureFlagVariantKey(FEATURE_FLAGS.SENTENCE_REFERENCE)
	const [promptInput, setPromptInput] = useState('')

	const handleUserType = () => {
		dispatch(setDrafts({ docID: props?.docID, drafts: [] }))
	}

	const editor = useEditor(
		{
			extensions: [
				StarterKit,
				EditableSpan,
				HoverMenuNode,
				SlashCommand,
				Placeholder.configure({
					placeholder: 'Start typing here and autocomplete the rest...',
				}),
				TypingExtension.configure({
					onUserType: handleUserType, // Pass the function
				}),
				Highlight.configure({ multicolor: true }),
				CopyEventHandler.extend({
					addOptions() {
						return {
							onCopy: () => {
								posthog.capture('copied-text')
							},
						}
					},
				}),
				Ai.configure({
					token: jwtToken,
					autocompletion: true,
					onSuccess: (context) => {
						if ((context?.response?.length ?? 0) > 0) {
							setShowAutocompleteMenu(true)
							posthog.capture('autocomplete-triggered')
						}
					},
					aiStreamResolver: async ({ action, text, textOptions, extensionOptions }) => {
						if (text.endsWith('/')) {
							return new ReadableStream({
								start(controller) {
									controller.close()
								},
							})
						}
						const requestOptions = {
							method: 'POST',
							headers: { 'Content-Type': 'application/json' },
							body: JSON.stringify({
								context: text,
								topic: documentState.title,
								docId: props?.docID,
								refStyle: documentState.userWriterSelections?.refStyle,
								use4o: false,
							}),
						}
						const response = await fetch(process['env']['REACT_APP_API_ROOT'] + '/essay/autocomplete/', requestOptions)
						const json = await response?.json()
						if (!response.ok) {
							throw new Error(`${response.status} ${json?.message}`)
						}
						let completion = json?.completion

						return new ReadableStream({
							start(controller) {
								controller.enqueue(new TextEncoder().encode(completion))
								controller.close()
							},
						})
					},
				}),
			],
			content: documentState?.content ?? CONSTANTS.EMPTY_DOCUMENT,
			autofocus: true,
			onSelectionUpdate: ({ editor }) => {
				setShowAutocompleteMenu(false)
				try {
					const { $from } = editor.state.selection
					const parentNode = $from.parent
					if (parentNode && parentNode.type?.name === 'paragraph' && parentNode.textContent.length > 100) {
						setShowEditorBar(true)
					} else {
						setShowEditorBar(false)
					}
				} catch (e) {
					setShowEditorBar(false)
				}
			},
			editable:
				documentState &&
				!documentState.isGenerating &&
				!documentState.isTypingAddMore &&
				!documentState.isTypingEssay &&
				!askAIGenerating,
			onUpdate({ editor }) {
				if (!documentState.isGenerating && !documentState.isTypingAddMore && !documentState.isTypingEssay) {
					dispatch(setShowNav({ value: false }))
				}
				if (editor?.getHTML() !== lastEditorContent) {
					dispatch(setContent({ docID: props?.docID, content: editor?.getHTML() }))
					dispatch(setJSONContent({ docID: props?.docID, jsonContent: editor.getJSON() }))
				}
				setLastEditorContent(editor?.getHTML() ?? CONSTANTS.EMPTY_DOCUMENT)
			},
		},
		[]
	)
	const data = useData()
	const actions = useContentItemActions(editor, data.currentNode, data.currentNodePos)

	useEffect(() => {
		if (!editor) return
		editor?.setEditable(
			documentState &&
				!documentState.isGenerating &&
				!documentState.isTypingAddMore &&
				!documentState.isTypingEssay &&
				!askAIGenerating
		)
		//Need specific control over the editor editable state, depending on updates to loading variables
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [documentState?.isGenerating, documentState?.isTypingAddMore, documentState?.isTypingEssay, askAIGenerating])

	//Update editor content when redux content changes
	useEffect(() => {
		if (!editor) return
		const { from, to } = editor.state.selection
		editor.commands.setContent(documentState?.content ?? CONSTANTS.EMPTY_DOCUMENT, false, {
			preserveWhitespace: 'full',
		})
		dispatch(setJSONContent({ docID: props?.docID, jsonContent: editor.getJSON() }))
		editor.commands.setTextSelection({ from, to })

		//This useEffect should ONLY trigger when documentState content is updated
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [documentState.content])

	//update current selection when user changes selection
	useEffect(() => {
		if (!editor) return
		const { from, to } = editor.state.selection
		setCurrSelection(editor.state.doc.textBetween(from, to))

		//This useEffect should ONLY trigger when editor selection is updated
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [editor?.state.selection])

	const resetAskAIOptions = () => {
		if (!editor) return
		setAskAIBubbleMenuOpen(false)
		setStagedContent('')
		setPromptInput('')
		setAskAIDropdownType(null)
		editor.commands.setTextSelection({ from: 0, to: 0 })
	}

	const handleAskAIDropdownButtonClick = (e: any, options: { type?: string; prompt?: string }) => {
		e.preventDefault()
		setAskAIDropdownType(options.type ?? null)
		if (!editor) return

		// Check if current selection is fewer than 100 characters
		const currentSelection = editor.state.doc.textBetween(editor.state.selection.from, editor.state.selection.to)

		if (currentSelection.length < 100) {
			// Select the entire current paragraph
			const { $from } = editor.state.selection

			// Find the paragraph node that contains the current position
			let paragraphStart = $from.start($from.depth)
			let paragraphEnd = $from.end($from.depth)

			// If we're not in a paragraph, try to find the closest paragraph
			if ($from.parent.type.name !== 'paragraph') {
				// Look for paragraph at different depths
				for (let depth = $from.depth; depth >= 0; depth--) {
					const node = $from.node(depth)
					if (node.type.name === 'paragraph') {
						paragraphStart = $from.start(depth)
						paragraphEnd = $from.end(depth)
						break
					}
				}
			}

			// Set selection to the entire paragraph and focus the editor
			editor.chain().focus().setTextSelection({ from: paragraphStart, to: paragraphEnd }).run()

			// Get the paragraph text after selection is set
			const paragraphText = editor.state.doc.textBetween(paragraphStart, paragraphEnd)
			setCurrSelection(paragraphText)

			// Use setTimeout to ensure selection is applied before proceeding
			setTimeout(() => {
				setAskAIDropdownOpen(false)
				if (options.type === 'Cite') {
					setAddReferenceBubbleMenuOpen(true)
				} else {
					setAskAIBubbleMenuOpen(true)
					getAskAIResponse(options, paragraphText)
				}
			}, 100)
		} else {
			// If selection is already long enough, proceed immediately
			setAskAIDropdownOpen(false)
			if (options.type === 'Cite') {
				setAddReferenceBubbleMenuOpen(true)
			} else {
				setAskAIBubbleMenuOpen(true)
				if (options.type !== 'Customize') {
					getAskAIResponse(options)
				}
			}
		}
	}

	const renderStyleButtons = () => {
		if (!editor) return null
		return (
			<>
				{!askAIBubbleMenuOpen && (
					<>
						<div id="styleButtonContainer">
							<button
								onClick={() => editor.chain().focus().toggleBold().run()}
								className={`border-r border-gray-300 bubble-item${editor.isActive('bold') ? ' is-active' : ''}`}
							>
								<strong>{'B'}</strong>
							</button>
							<button
								onClick={() => editor.chain().focus().toggleItalic().run()}
								className={`border-r border-gray-300 bubble-item${editor.isActive('italic') ? ' is-active' : ''}`}
							>
								<em>{'i'}</em>
							</button>
							<button
								onClick={() => editor.chain().focus().toggleHighlight({ color: '#c9f2f1' }).run()}
								className={`border-r border-gray-300 bubble-item${editor.isActive('highlight') ? ' is-active' : ''}`}
							>
								<mark>{'H'}</mark>
							</button>
							<button
								onClick={() => editor.chain().focus().toggleStrike().run()}
								className={`border-r border-gray-300 bubble-item${editor.isActive('strike') ? ' is-active' : ''}`}
							>
								{<StrikethroughSIcon />}
							</button>
							<button
								onClick={() => editor.chain().focus().toggleHeading({ level: 6 }).run()}
								className={`header bubble-item${editor.isActive('heading') ? ' is-active' : ''}`}
							>
								{'Header'}
							</button>
							{editorBarFF !== 'test' &&
								currSelection.split(' ').length >= 5 &&
								currSelection.split(' ').length <= 200 && (
									<button
										onClick={() => {
											setAskAIDropdownOpen(!askAIDropdownOpen)
											posthog.capture('ask-ai-button-clicked')
										}}
										className={`ask-ai bubble-item${askAIDropdownOpen ? ' is-active' : ''}`}
										ref={closeAskAIDropdown}
									>
										Ask AI
										<AutoAwesomeIcon />
									</button>
								)}
						</div>
						<Dropdown
							open={askAIDropdownOpen}
							setOpen={(val: boolean) => {
								setAskAIDropdownOpen(val)
							}}
							body={askAiDropdownContent}
							closeButton={closeAskAIDropdown}
							className="askAIDropdown"
						/>
					</>
				)}
			</>
		)
	}

	async function getAskAIResponse(options: { type?: string; prompt?: string }, currSelectionOverride?: string) {
		if (!editor) return
		posthog.capture('ask-ai-triggered', {
			type: options.type,
		})
		setAskAIGenerating(true)

		const requestOptions = {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({
				title: documentState.title,
				input: htmlToString(currSelectionOverride ?? currSelection),
				type: options.type,
				prompt: options.prompt,
				userId: user.id ?? userIDCookie,
				docId: props?.docID,
			}),
		}
		fetch(process['env']['REACT_APP_API_ROOT'] + '/essay/ask-ai/', requestOptions)
			.then((res) => res.json())
			.then((result: string) => {
				setStagedContent(result)
				setAskAIGenerating(false)
			})
			.catch((e) => {
				setAskAIGenerating(false)
				setErrorModalOpen(true)
				resetAskAIOptions()
			})
	}

	const askAiDropdownContent = (
		<>
			{Object.entries(ASK_AI_TYPES).map(([key, value]) => {
				return (
					<div
						className="ask-ai-dropdown-item"
						key={key + value}
						onClick={(e) => handleAskAIDropdownButtonClick(e, { type: value })}
					>
						<div className="ask-ai-dropdown-item-value fancy-font">{value}</div>
						{/* @ts-ignore Can fix this type error when we port constants.js to constants.ts*/}
						<div className="ask-ai-dropdown-item-desc">{ASK_AI_DESCRIPTIONS[value]}</div>
					</div>
				)
			})}
		</>
	)

	const renderAskAIBubbleMenu = () => {
		if (!editor) return null

		if (askAIDropdownType === 'Customize') {
			return (
				<div className="flex flex-col gap-2 py-0 px-0 items-end w-full h-full">
					<div className="text-sm w-full text-left font-medium">How do you want to customize this text?</div>
					<TextareaAutosize
						placeholder="E.g. Make this more professional"
						autoFocus
						onChange={(e) => {
							setPromptInput(e.target.value)
						}}
						minRows={7}
						maxRows={7}
						value={promptInput}
						className="px-3 py-2 border border-gray-500 rounded outline-none resize-none grow focus:border-gray-900 w-full"
					/>
					<div className="flex gap-2">
						<Button
							type="ghost"
							onClick={(e) => {
								resetAskAIOptions()
							}}
						>
							Cancel
						</Button>

						<Button
							type="default"
							onClick={(e) => handleAskAIDropdownButtonClick(e, { prompt: promptInput })}
							disabled={promptInput.length === 0}
						>
							Customize
						</Button>
					</div>
				</div>
			)
		}
		return (
			<>
				<div className="askAIResultDropdownOldText">
					{currSelection.length > 250 ? currSelection.slice(0, 200) + '...' : currSelection}
				</div>
				{askAIGenerating ? (
					<div className="topicDivider oscillating-bar"></div>
				) : (
					<div className="border-t border-gray-200"></div>
				)}
				<div className="askAIResultDropdownNewText">
					{askAIGenerating ? (
						<div>
							{[...Array(8)].map((_, index) => (
								<Skeleton key={index} variant="text" width="100%" height={25} animation="wave" />
							))}
						</div>
					) : (
						<Diff oldStr={currSelection} newStr={stagedContent} />
					)}
				</div>

				<div className="askAIResultDropdownButtonContainer">
					<Button
						type="outline"
						disabled={askAIGenerating}
						onClick={(e) => {
							if (askAIGenerating) return
							try {
								if (!editor || !stagedContent) return
								//replace selection with staged contents
								let startSelection = editor.state.selection.from
								if (!startSelection) return
								editor.commands.deleteSelection()
								editor.commands.insertContentAt(startSelection, completionToHTMLString(stagedContent))
								resetAskAIOptions()
							} catch (e) {
								console.error(e)
							}
						}}
					>
						<span>Accept Changes</span>
					</Button>
					<Button
						className="ml-1"
						type="ghost"
						disabled={askAIGenerating}
						onClick={(e) => {
							if (askAIGenerating) return
							resetAskAIOptions()
						}}
					>
						<span>Cancel</span>
					</Button>
				</div>
			</>
		)
	}

	return (
		<>
			<CompleteModal open={showCompleteModal} closeModal={() => setShowCompleteModal(false)} />
			<ErrorModal open={errorModalOpen} closeModal={() => setErrorModalOpen(false)} />
			<div>
				<div>
					{editor &&
						documentState &&
						!documentState.isGenerating &&
						!documentState.isTypingAddMore &&
						!documentState.isTypingEssay && (
							<div>
								<BubbleMenu
									editor={editor}
									tippyOptions={{ duration: 100 }}
									shouldShow={({ editor }) => {
										return !askAIBubbleMenuOpen &&
											editor.isEditable &&
											editor &&
											editor.state &&
											editor.state.selection &&
											editor.state.selection.content()
											? editor.state.selection.content().size > 0
											: false
									}}
								>
									{renderStyleButtons()}
								</BubbleMenu>
								<ControlledBubbleMenu
									editor={editor}
									open={askAIBubbleMenuOpen}
									children={renderAskAIBubbleMenu}
									onClickAway={() => {
										resetAskAIOptions()
									}}
								/>
								<ControlledBubbleMenu
									editor={editor}
									open={addReferenceBubbleMenuOpen}
									children={
										<SelectReferenceMenu
											editor={editor}
											currentSelection={currSelection}
											setMenuOpen={setAddReferenceBubbleMenuOpen}
										/>
									}
									onClickAway={() => {
										setAddReferenceBubbleMenuOpen(false)
										resetAskAIOptions()
									}}
								/>
							</div>
						)}

					{editor &&
						documentState &&
						!documentState.isGenerating &&
						!documentState.isTypingAddMore &&
						!documentState.isTypingEssay && (
							<DragHandle
								pluginKey="ContentItemMenu"
								editor={editor}
								onNodeChange={data.handleNodeChange}
								className={`relative pb-4 h-full text-xl left-6 pt-0.5`}
							>
								<div
									className={`p-1 rounded hover:bg-gray-100 cursor-pointer text-gray-700 hover:text-gray-900 md:block hidden`}
									onClick={() => {
										posthog.capture('insert-more-plus-clicked')
										actions.handleAdd()
									}}
								>
									{LuPlus({})}
								</div>
							</DragHandle>
						)}
					{props?.docID && (
						<Autocomplete
							editor={editor}
							docID={props.docID}
							showAutocompleteMenu={showAutocompleteMenu}
							setShowAutocompleteMenu={setShowAutocompleteMenu}
						/>
					)}
				</div>
				{documentState && !documentState.isGenerating && (
					<EditorContent id="tiptap-editor" editor={editor} className="v2" />
				)}
				{documentState && documentState.setup && props && editor && (
					<AddMore type={props.type} docID={props.docID} isFixed={false} editor={editor} />
				)}
			</div>
			{(currSelection.length > (sentenceReferenceFF ? 50 : 100) || showEditorBar) && (
				<EditorBar
					onEditButtonClick={handleAskAIDropdownButtonClick}
					onCompleteClick={() => setShowCompleteModal(true)}
				/>
			)}
		</>
	)
}

export default Document
